import { memo } from 'react'
import { useFormState } from 'react-final-form'
import { FormItem, Label } from '@cargomart/ui'
import { compose, required } from 'forms/validators'
import { useRouteLimits } from 'modules/terminal/hooks'
import { OrderCreateCargo } from 'schema'
import { FieldNumber } from 'shared/forms/FieldNumber'

import { TOrderCreateCustomer } from '../../types'
import {
  MAX_HEIGHT_GRADE_IN_CENTIMETER,
  MAX_LENGTH_GRADE_IN_CENTIMETER,
  MAX_WEIGHT_GRADE,
  MAX_WIDTH_GRADE_IN_CENTIMETER,
  validationSeparate,
  validationSeparateSpecial
} from './constants'
import { minMaxWithTerminalLimit } from './validators'

interface IProps {
  name: string
  type: OrderCreateCargo.type
  className?: string
}

export const OrderCargoSeparateItemSize = memo<IProps>(function OrderCargoSeparateItemSize({ name, type }) {
  const fieldClassName = 'w-full [&>*:first-child]:!text-on-surface [&>*]:!text-on-surface-variant'
  const {
    values: { customer }
  } = useFormState<{ customer: TOrderCreateCustomer }>()
  const { hasSpecialCargoLimits } = customer || {}

  const routeLimits = useRouteLimits()

  return (
    <>
      {type === OrderCreateCargo.type.CUSTOM && (
        <div className="grid grid-cols-1 gap-x-4 @xs:grid-cols-2 @sm:grid-cols-4">
          <FormItem label={<Label className="pt-2">Длина</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_LENGTH_GRADE_IN_CENTIMETER}
              name={`${name}.customLength`}
              postfix="см"
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.custom.length,
                  validationSeparateSpecial.custom.length,
                  routeLimits.maxLengthCm,
                  'см'
                )
              )}
            />
          </FormItem>
          <FormItem label={<Label className="pt-2">Ширина</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_WIDTH_GRADE_IN_CENTIMETER}
              name={`${name}.customWidth`}
              postfix="см"
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.custom.width,
                  validationSeparateSpecial.custom.width,
                  routeLimits.maxWidthCm,
                  'см'
                )
              )}
            />
          </FormItem>
          <FormItem label={<Label className="pt-2">Высота</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_HEIGHT_GRADE_IN_CENTIMETER}
              name={`${name}.customHeight`}
              postfix="см"
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.custom.height,
                  validationSeparateSpecial.custom.height,
                  routeLimits.maxHeightCm,
                  'см'
                )
              )}
            />
          </FormItem>
          <FormItem label={<Label className="pt-2">Вес</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_WEIGHT_GRADE}
              name={`${name}.customWeight`}
              postfix="кг"
              scale={3}
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.custom.weight,
                  validationSeparateSpecial.custom.weight,
                  routeLimits.maxWeightKg,
                  'кг'
                )
              )}
            />
          </FormItem>
        </div>
      )}
      {type === OrderCreateCargo.type.PALLET && (
        <div className="grid grid-cols-1 gap-x-4 @xs:grid-cols-2 @sm:grid-cols-4">
          <FormItem label={<Label className="pt-2">Длина</Label>}>
            <FieldNumber
              disabled
              isShowTooltip
              className={fieldClassName}
              max={MAX_LENGTH_GRADE_IN_CENTIMETER}
              name={`${name}.palletLength`}
              postfix="см"
              validate={required}
            />
          </FormItem>
          <FormItem label={<Label className="pt-2">Ширина</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              disabled={!hasSpecialCargoLimits}
              max={MAX_WIDTH_GRADE_IN_CENTIMETER}
              name={`${name}.palletWidth`}
              postfix="см"
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.pallet.width,
                  validationSeparateSpecial.pallet.width,
                  routeLimits.maxWidthCm,
                  'см'
                )
              )}
            />
          </FormItem>
          <FormItem label={<Label className="pt-2">Высота</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_HEIGHT_GRADE_IN_CENTIMETER}
              name={`${name}.palletHeight`}
              postfix="см"
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.pallet.height,
                  validationSeparateSpecial.pallet.height,
                  routeLimits.maxHeightCm,
                  'см'
                )
              )}
            />
          </FormItem>
          <FormItem label={<Label className="pt-2">Вес</Label>}>
            <FieldNumber
              isShowTooltip
              className={fieldClassName}
              max={MAX_WEIGHT_GRADE}
              name={`${name}.palletWeight`}
              postfix="кг"
              scale={3}
              validate={compose(
                required,
                minMaxWithTerminalLimit(
                  validationSeparate.pallet.weight,
                  validationSeparateSpecial.pallet.weight,
                  routeLimits.maxWeightKg,
                  'кг'
                )
              )}
            />
          </FormItem>
        </div>
      )}
    </>
  )
})
